import { Suspense, lazy, useEffect, useState } from "react";
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import CookieBanner from "./components/CookieBanner";
import DemoModal from "./components/DemoModal";
import ErrorBoundary from "./components/ErrorBoundary";
import LanguageSwitcher from "./components/LanguageSwitcher";
import ScrollToTop from "./components/ScrollToTop";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import "./styles.css";

function useCurrentLanguage() {
  const { language } = useLanguage();
  return language;
}

// Loading fallback component
const PageLoader = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      gap: "1rem",
    }}
  >
    <div
      style={{
        width: "48px",
        height: "48px",
        border: "4px solid var(--color-border)",
        borderTop: "4px solid var(--color-primary)",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
    <p style={{ color: "var(--color-text-secondary)", fontSize: "0.875rem" }}>
      Загрузка...
    </p>
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

// Lazy-loaded page components
const Home = lazy(() => import("./components/Home"));
const CaseStudyPage = lazy(() => import("./components/PilotPage"));
const RoadmapPage = lazy(() => import("./components/RoadmapPage"));
const DocsPage = lazy(() => import("./components/DocsPage"));
const ResourcesPage = lazy(() => import("./components/ResourcesPage"));
const NotFound = lazy(() => import("./components/NotFound"));
const PrivacyPage = lazy(() => import("./components/PrivacyPage"));
const ReferatPage = lazy(() => import("./components/ReferatPage"));

function AppContent() {
  const [darkMode, setDarkMode] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [language, setLanguage] = useState("ru");
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const getActiveTab = () => {
    if (location.pathname === "/case") return "case";
    if (location.pathname === "/roadmap") return "roadmap";
    if (location.pathname === "/docs") return "docs";
    if (location.pathname === "/resources") return "resources";
    return "home";
  };

  const activeTab = getActiveTab();

  const handleNavigate = (path) => {
    navigate(path);
  };

  const openDemoModal = () => setShowDemoModal(true);
  const closeDemoModal = () => setShowDemoModal(false);

  useEffect(() => {
    const saved = localStorage.getItem("tcm-dark-mode");
    const isDark = saved ? JSON.parse(saved) : false;
    setDarkMode(isDark);
    document.documentElement.setAttribute(
      "data-bs-theme",
      isDark ? "dark" : "light",
    );
  }, []);

  // Auto-detect language based on browser/region
  useEffect(() => {
    const savedLanguage = localStorage.getItem("tcm-language");
    if (savedLanguage) {
      setLanguage(savedLanguage);
    } else {
      const browserLang = navigator.language || navigator.userLanguage;
      const detectedLang = browserLang.startsWith("en") ? "en" : "ru";
      setLanguage(detectedLang);
      localStorage.setItem("tcm-language", detectedLang);
    }
  }, []);

  useEffect(() => {
    const titles = {
      home: "ТСМ — Система мониторинга терминальной сети",
      case: "Кейс внедрения ТСМ — Мониторинг банкоматов",
      roadmap: "Roadmap развития ТСМ — План 2025-2026",
      docs: "Документация ТСМ — Protobuf, API, примеры",
      resources: "Документы и публикации ТСМ — Свидетельство и статьи",
      referat: "Реферат — ТСМ — Техническая система мониторинга",
    };
    document.title = titles[activeTab] || titles.home;
  }, [activeTab]);

  useEffect(() => {
    const descriptions = {
      home: "Профессиональное решение для контроля банкоматов, платёжных терминалов, POS-устройств и IoT-инфраструктуры. Снижение OPEX на 45%, ROI 340%.",
      case: "Профессиональное решение для контроля банкоматов, платёжных терминалов, POS-устройств и IoT-инфраструктуры. Снижение OPEX на 45%, ROI 340%.",
      roadmap:
        "Профессиональное решение для контроля банкоматов, платёжных терминалов, POS-устройств и IoT-инфраструктуры. Снижение OPEX на 45%, ROI 340%.",
      docs: "Профессиональное решение для контроля банкоматов, платёжных терминалов, POS-устройств и IoT-инфраструктуры. Снижение OPEX на 45%, ROI 340%.",
      resources:
        "Профессиональное решение для контроля банкоматов, платёжных терминалов, POS-устройств и IoT-инфраструктуры. Снижение OPEX на 45%, ROI 340%.",
      referat: "Реферат для регистрации программы для ЭВМ ТСМ — Техническая система мониторинга терминальной сети.",
    };
    let meta = document.querySelector("meta[name='description']");
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", descriptions[activeTab] || descriptions.home);
  }, [activeTab]);

  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem("tcm-dark-mode", JSON.stringify(newMode));
    document.documentElement.setAttribute(
      "data-bs-theme",
      newMode ? "dark" : "light",
    );
  };

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleMobileNav = (path) => {
    handleNavigate(path);
    closeMobileMenu();
  };

  const navItems = [
    { id: "home", label: t("nav.home"), path: "/" },
    { id: "case", label: t("nav.case"), path: "/case" },
    { id: "roadmap", label: t("nav.roadmap"), path: "/roadmap" },
    { id: "docs", label: t("nav.docs"), path: "/docs" },
    { id: "resources", label: t("nav.resources"), path: "/resources" },
  ];

  return (
    <>
      {/* Navbar */}
      <nav className="navbar-custom">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center w-100">
            <a href="/" className="navbar-brand-custom">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="32" height="32" style={{verticalAlign: 'middle', marginRight: '8px'}}>
                <defs>
                  <linearGradient id="logoBg" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style={{stopColor:'#1a3a5f',stopOpacity:1}} />
                    <stop offset="100%" style={{stopColor:'#0d6efd',stopOpacity:1}} />
                  </linearGradient>
                </defs>
                <rect width="512" height="512" rx="100" fill="url(#logoBg)"/>
                <text x="256" y="350" fontFamily="Arial, sans-serif" fontSize="280" fontWeight="bold" fill="white" textAnchor="middle">ТСМ</text>
              </svg>
              ТСМ
            </a>

            {/* Desktop nav */}
            <div className="d-none d-md-flex align-items-center gap-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  className={`nav-link-custom ${activeTab === item.id ? "active" : ""}`}
                  onClick={() => handleNavigate(item.path)}
                >
                  {item.label}
                </button>
              ))}

              <button
                className="btn btn-primary-custom ms-2"
                onClick={openDemoModal}
              >
                {t("nav.requestDemo")}
              </button>

              <button
                className="btn"
                onClick={toggleDarkMode}
                style={{
                  padding: "0.5rem 0.625rem",
                  minWidth: "auto",
                  background: "transparent",
                  border: "none",
                  color: "var(--color-text-secondary)",
                  fontSize: "1.125rem",
                  cursor: "pointer",
                  transition: "color 0.2s",
                }}
                title={darkMode ? t("theme.light") : t("theme.dark")}
              >
                {darkMode ? "☀" : "☾"}
              </button>

              <LanguageSwitcher />
            </div>

            {/* Mobile menu button */}
            <button
              className="d-md-none btn mobile-menu-toggle"
              onClick={toggleMobileMenu}
              style={{
                background: "none",
                border: "none",
                padding: "0.75rem",
                color: "var(--color-text)",
                fontSize: "1.75rem",
                touchAction: "manipulation",
              }}
              aria-label={t("nav.menu")}
            >
              {mobileMenuOpen ? "✕" : "☰"}
            </button>
          </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
              <div
                className="mobile-menu d-md-none"
                style={{
                  marginTop: "1rem",
                  paddingTop: "1rem",
                  borderTop: "1px solid var(--color-border)",
                  paddingBottom: "1rem",
                }}
              >
                <div className="d-flex flex-column gap-2">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      className={`nav-link-custom text-start w-100 py-3 px-3 ${activeTab === item.id ? "active" : ""}`}
                      style={{
                        fontSize: "1.125rem",
                        borderRadius: "var(--radius)",
                        touchAction: "manipulation",
                      }}
                      onClick={() => handleMobileNav(item.path)}
                    >
                      {item.label}
                    </button>
                  ))}
                  <button
                    className="btn btn-primary-custom w-100 mt-2 py-3"
                    style={{ fontSize: "1rem" }}
                    onClick={() => {
                      openDemoModal();
                      closeMobileMenu();
                    }}
                  >
                    {t("nav.requestDemo")}
                  </button>
                  <button
                    className="btn w-100 mt-1 py-2"
                    onClick={() => {
                      toggleDarkMode();
                    }}
                    style={{
                      padding: "0.75rem",
                      background: "transparent",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius)",
                      color: "var(--color-text-secondary)",
                      fontSize: "1rem",
                      cursor: "pointer",
                      textAlign: "start",
                      touchAction: "manipulation",
                    }}
                  >
                    {darkMode ? `☀ ${t("theme.light")}` : `☾ ${t("theme.dark")}`}
                  </button>
                  <div className="mt-2">
                    <LanguageSwitcher mobile={true} />
                  </div>
                </div>
              </div>
            )}
        </div>
      </nav>

      {/* Demo Modal */}
      <DemoModal show={showDemoModal} onClose={closeDemoModal} />

      {/* Cookie Banner */}
      <CookieBanner language={language} />

      {/* Scroll to Top Button */}
      <ScrollToTop />

      {/* Routes with Error Boundary & Suspense */}
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route
              path="/"
              element={<Home darkMode={darkMode} onDemoClick={openDemoModal} />}
            />
            <Route
              path="/case"
              element={<CaseStudyPage darkMode={darkMode} />}
            />
            <Route
              path="/roadmap"
              element={<RoadmapPage darkMode={darkMode} />}
            />
            <Route path="/docs" element={<DocsPage darkMode={darkMode} />} />
            <Route
              path="/resources"
              element={<ResourcesPage darkMode={darkMode} />}
            />
            <Route
              path="/privacy"
              element={<PrivacyPage language={language} />}
            />
            <Route
              path="/referat"
              element={<ReferatPage darkMode={darkMode} />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      {/* Footer */}
      <footer className="footer-custom">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <p className="footer-text">
              © {new Date().getFullYear()} {t("footer.rights")}
            </p>
            <div className="d-flex gap-3 flex-wrap">
              <a
                href="https://businessarchitecture.ru"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-text"
                style={{ textDecoration: "none" }}
              >
                businessarchitecture.ru
              </a>
              <a
                href="https://businessarchitecture.ru/author"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-text"
                style={{ textDecoration: "none" }}
              >
                Автор
              </a>
              <a
                href="https://t.me/businessarchitecture_ru"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-text"
                style={{ textDecoration: "none" }}
              >
                Telegram
              </a>
              <a
                href="https://dzen.ru/businessarchitecture"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-text"
                style={{ textDecoration: "none" }}
              >
                Дзен
              </a>
              <a
                href="/privacy"
                className="footer-text"
                style={{ textDecoration: "none" }}
              >
                {language === "ru"
                  ? "Политика конфиденциальности"
                  : "Privacy Policy"}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </Router>
  );
}
